import { activateDependentById } from '@brands/services/identity/activateDependentById';
import { listDependents } from '@brands/services/identity/listDependents';
import { Dependent } from '@brands/services/identity/types/Dependent';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { setDependents } from '@brands/store/slices/dependentsSlice';
import { checkProfileForm } from '@brands/Utils/checkExternalForms';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { IconType } from 'react-icons';
import { BsToggleOn } from 'react-icons/bs';
import { FiEdit } from 'react-icons/fi';
import { useParams } from 'react-router-dom';

import { Loading } from '../../../../../Components/LoadingSpinner/Loading';
import ProfileImage from '../../../../../Components/ProfileImage/ProfileImage';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/useReduxHook';
import { UserRoleName } from '../../../../../services/identity/types/UserProfile';
import styles from './styles.module.scss';
import addFilled from '../Dependent/AddFilled.svg';

type ReadOnlyProfileProps = {
  onEditDependent: (id: number) => void;
  addDependent: () => void;
  reviewProfile: boolean;
};

const ReadOnlyDependent = ({
  onEditDependent,
  addDependent,
  reviewProfile,
}: ReadOnlyProfileProps): JSX.Element => {
  const { userId } = useParams();
  const { userInfo } = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const { selectedPatient } = useAppSelector(selectPatient);
  const [dependentList, setDependentList] = useState<Dependent[]>([]);
  const [deactivatedDependentList, setDeactivatedDependentList] = useState<Dependent[]>([]);
  const [deactivatedDependentListVisible, setDeactivatedDependentListVisible] = useState(false);

  const setAllDependents = async (): Promise<void> => {
    const listOfDependents = await listDependents(!reviewProfile ? userInfo.id : userId, { include_deleted: true });
    if (listOfDependents.length > 0) {
      setDependentList(listOfDependents);
      dispatch(
        setDependents(
          listOfDependents
            .slice()
            .sort((a, b) => b.id - a.id)
            .map((dependent) => ({
              value: dependent.id.toString(),
              label: dependent.full_name,
              status: dependent.status,
            })),
        ),
      );
    } else {
      setDependentList([]);
      dispatch(setDependents([]));
    }
    if (listOfDependents.filter((dependent) => dependent?.status === 'Deactivated').length > 0) {
      setDeactivatedDependentList(listOfDependents);
    } else {
      setDeactivatedDependentList([]);
    }
  };

  const onReactivateDependent = async (id: number): Promise<void> => {
    await activateDependentById(
      userInfo.role.name === UserRoleName.Admin ||
        userInfo.role.name === UserRoleName.SuperAdmin ||
        userInfo.role.name === UserRoleName.OrganizationAdmin
        ? selectedPatient.id
        : userInfo.id,
      id,
    );
    setAllDependents();
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await setAllDependents();
      } catch (error) {
        displayErrorDetails(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isAdminViewing =
    userInfo.role.name === UserRoleName.Admin ||
    userInfo.role.name === UserRoleName.SuperAdmin ||
    userInfo.role.name === UserRoleName.OrganizationAdmin;
  const canAddDependent = isAdminViewing || checkProfileForm(userInfo, null);

  const renderActionButton = (): JSX.Element => {
    return (
      <Button
        className={`fs-unmask ${styles.addBtn}`}
        type="button"
        variant="contained"
        data-testid="profile-add-dependent"
        onClick={canAddDependent ? addDependent : undefined}
        disabled={!canAddDependent}
      >
        <img src={addFilled} alt="" aria-hidden />
        Add dependent
      </Button>
    );
  };


  const renderDependentList = (
    list: Dependent[],
    status: string,
    actionText: string,
    actionIcon: IconType,
    actionHandler: (id: number) => void,
  ): JSX.Element => {
    if (list.length !== 0) {
      return (
        <ul className={styles.dependentsList} style={{ paddingLeft: '0', gap: '20px' }}>
          {list
            .filter((dependents) => dependents.status === status)
            .map((dependent) => (
              <li key={dependent?.id} className={styles.dependentItem} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
                <ProfileImage
                  id={dependent.id.toString()}
                  reviewProfile
                  isUpload={false}
                  size="48px"
                  givingUserProfile={{
                    first_name: dependent?.basic_info?.first_name,
                    last_name: dependent?.basic_info?.last_name,
                  }}
                />
                <div className={styles.dependentInfo}>
                  <div className={`fs-exclude ${styles.dependentName}`}>{dependent.full_name || '-'}</div>
                  <button className={styles.editBtn} type="button" onClick={() => actionHandler(dependent.id)} style={{ color: '#1976D2' }}>
                  {actionText}
                  </button>
                </div>
              </li>
            ))}
        </ul>
      );
    }
    return <div className={styles.noDependent}>No Dependents</div>;
  };

  const renderTitle = (): JSX.Element => (
    <>
      <div className={`${styles.titleContainer} ${styles.mobileFlexRevCol} ${styles.mobileAddDependent}`}>
        <span className={`fs-unmask ${styles.title}`}>Dependents</span>
        {renderActionButton()}
      </div>
      {!canAddDependent && <span>You need to complete your profile to add a dependent.</span>}
    </>
  );

  const renderDependentSection = (): JSX.Element => {
    if (loading) {
      return <Loading dotted />;
    }

    if (dependentList.length !== 0) {
      return renderDependentList(dependentList, 'Active', 'Edit profile', FiEdit, onEditDependent);
    }

    return <div className={styles.noDependent}>No Dependents</div>;
  };

  const renderDeactivatedDependents = (): JSX.Element => {
    if (loading) {
      return <Loading dotted />;
    }

    if (deactivatedDependentList.length !== 0) {
      return (
        <>
          <Button
            variant="text"
            className={styles.readOnlyProfileShowDeactivatedDependentsButton}
            onClick={() => setDeactivatedDependentListVisible(!deactivatedDependentListVisible)}
          >
            {deactivatedDependentListVisible ? 'Hide Deactivated Dependents' : 'View deactivated dependents'}
          </Button>
          {deactivatedDependentListVisible &&
            renderDependentList(
              deactivatedDependentList,
              'Deactivated',
              'Reactivate',
              BsToggleOn,
              onReactivateDependent,
            )}
        </>
      );
    }

    return <div className={styles.noDependent} />;
  };

  return (
    <div className={styles.readOnlyProfileContainer} style={{ marginTop: '32px', gap: '0px' }}>
      {renderTitle()}
      {renderDependentSection()}
      <div className={styles.readOnlyProfileDeactivatedDependents}>{renderDeactivatedDependents()}</div>
    </div>
  );
};

export default ReadOnlyDependent;
